import { useEffect, useState } from 'react';
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Card,
  Link,
  Input,
  Button,
} from '@nextui-org/react';
import { supabase } from 'common/src/supabase';
import AutomatedOfferViewer from '../../components/tasks/automation/AutomatedOfferViewer';
import ModalTask from '../../components/tasks/modalTask/modalTask';
import toast from 'react-hot-toast';
import { Tasks } from 'common/src/utils/types';
import { fetchTaskReturn } from 'common/src/api/tasks';

interface AutomatedTaskOffer {
  id: number;
  task_id: number;
  created_at: string;
  current_step: 'no-exp' | 'exp' | 'fully_offered';
  finished: boolean;
  last_run: string | null;
  task?: Tasks;
}

const AutomatedOffersPage = () => {
  const [offers, setOffers] = useState<AutomatedTaskOffer[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [viewMode, setViewMode] = useState<'table' | 'columns'>('table');
  const [stats, setStats] = useState({
    noExp: 0,
    exp: 0,
    fullyOffered: 0,
    completed: 0,
    finished: 0,
  });
  const [selectedTask, setSelectedTask] = useState<Tasks | null>(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        let query = supabase
          .from('automated_task_offer')
          .select(
            `
            *,
            task:tasks (
              id,
              category,
              description,
              status
            )
          `
          )
          .order('created_at', { ascending: false });

        if (fromDate) {
          query = query.gte('created_at', fromDate);
        }
        if (toDate) {
          query = query.lte('created_at', toDate);
        }

        const { data, error } = await query;

        if (error) throw error;
        setOffers(data || []);
        updateStats(data || []);
      } catch (err) {
        setError('Error al cargar las ofertas automatizadas');

        console.error(err);
      }
    };

    fetchOffers();

    // Subscribe to changes
    const subscription = supabase
      .channel('automated_task_offer_changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'automated_task_offer' },
        async (payload) => {
          // Fetch the complete record including task data
          if (payload.new) {
            const { data } = await supabase
              .from('automated_task_offer')
              .select(
                `
                *,
                task:tasks (
                  id,
                  category,
                  description,
                  status
                )
              `
              )
              // @ts-ignore
              .eq('id', payload.new.id)
              .single();

            setOffers((current) => {
              const updated = [...current];
              // @ts-ignore
              const index = updated.findIndex((o) => o.id === payload.new.id);
              if (index >= 0) {
                updated[index] = data;
              } else {
                updated.unshift(data);
              }
              updateStats(updated);
              return updated;
            });
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [fromDate, toDate]);

  const updateStats = (data: AutomatedTaskOffer[]) => {
    setStats({
      noExp: data.filter((o) => o.current_step === 'no-exp' && !o.finished).length,
      exp: data.filter((o) => o.current_step === 'exp' && !o.finished).length,
      fullyOffered: data.filter((o) => o.current_step === 'fully_offered' && !o.finished).length,
      completed: data.filter((o) => o.finished).length,
      finished: data.filter((o) => o.finished).length,
    });
  };

  const truncateDescription = (description: string) => {
    const words = description.split(' ').slice(0, 15).join(' ');
    return words + (description.split(' ').length > 15 ? '...' : '');
  };

  const renderOfferCard = (offer: AutomatedTaskOffer) => (
    <Card className="p-4 mb-4" key={offer.id}>
      <div className="flex flex-col gap-2">
        <div className="text-sm text-gray-500">
          {new Date(offer.created_at).toLocaleString('es-CL')}
        </div>
        <div>ID: {offer.id}</div>
        <div>Task ID: {offer.task_id}</div>
        <div>Categoría: {offer.task?.category}</div>
        <div>
          Descripción: {offer.task?.description ? truncateDescription(offer.task.description) : ''}
        </div>
        <div>Estado de Task: {offer.task?.status}</div>
        <div>
          Última ejecución:{' '}
          {offer.last_run ? new Date(offer.last_run).toLocaleString('es-CL') : 'Nunca'}
        </div>
        <div>Finalizado: {offer.finished ? 'Sí' : 'No'}</div>
        <AutomatedOfferViewer automated_task_offer_id={offer.id} />
      </div>
    </Card>
  );

  const handleTaskClick = async (taskId: number) => {
    try {
      const { data: taskData, error } = await fetchTaskReturn(taskId)

      if (error) throw error;

      setSelectedTask(taskData);
      setIsTaskModalOpen(true);
    } catch (err) {
      console.error('Error fetching task:', err);
      toast.error('Error al cargar la task');
    }
  };

  if (error) return <div>{error}</div>;

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-4">Ofertas Automatizadas</h1>
      <p>
        Visualización del algoritmo de autoasignación. Se le ofrecerán a 50 taskers a la vez, cada 3
        minutos, priorizando primero por taskers que no tengan un task asignado anteriormente, y
        ordenando por antiguedad.
      </p>

      <div className="flex gap-4 mb-8 mt-6">
        <Input
          type="date"
          label="Desde"
          placeholder="Fecha inicial"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
        />
        <Input
          type="date"
          label="Hasta"
          placeholder="Fecha final"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
        />
        <Button onClick={() => setViewMode(viewMode === 'table' ? 'columns' : 'table')}>
          {viewMode === 'table' ? 'Ver Columnas' : 'Ver Tabla'}
        </Button>
      </div>

      <div className="grid grid-cols-5 gap-4 mb-8">
        <Card className="p-4">
          <p className="text-lg font-bold">En Progreso (Sin Experiencia)</p>
          <p className="text-3xl">{stats.noExp}</p>
        </Card>
        <Card className="p-4">
          <p className="text-lg font-bold">En Progreso (Con Experiencia)</p>
          <p className="text-3xl">{stats.exp}</p>
        </Card>
        <Card className="p-4">
          <p className="text-lg font-bold">Ofertas Completamente Enviadas</p>
          <p className="text-3xl">{stats.fullyOffered}</p>
        </Card>
        <Card className="p-4">
          <p className="text-lg font-bold">Tasks Asignadas</p>
          <p className="text-3xl">{stats.completed}</p>
        </Card>
        <Card className="p-4">
          <p className="text-lg font-bold">Tasks No Asignadas</p>
          <p className="text-3xl">{stats.finished}</p>
        </Card>
      </div>

      {viewMode === 'table' ? (
        <Table aria-label="Tabla de ofertas automatizadas">
          <TableHeader>
            <TableColumn>Fecha y Hora</TableColumn>
            <TableColumn>ID</TableColumn>
            <TableColumn>ID de Task</TableColumn>
            <TableColumn>Categoría</TableColumn>
            <TableColumn>Descripción</TableColumn>
            <TableColumn>Estado de Oferta</TableColumn>
            <TableColumn>Última ejecución</TableColumn>
            <TableColumn>Finalizado</TableColumn>
            <TableColumn>Taskers</TableColumn>
          </TableHeader>
          <TableBody>
            {offers.map((offer) => (
              <TableRow key={offer.id}>
                <TableCell>{new Date(offer.created_at).toLocaleString('es-CL')}</TableCell>
                <TableCell>{offer.id}</TableCell>
                <TableCell>
                  <Link
                    className="cursor-pointer text-primary"
                    onPress={() => handleTaskClick(offer.task_id)}
                  >
                    {offer.task_id}
                  </Link>
                </TableCell>
                <TableCell>{offer.task?.category}</TableCell>
                <TableCell>
                  {offer.task?.description ? truncateDescription(offer.task.description) : ''}
                </TableCell>
                <TableCell>{offer.current_step}</TableCell>
                <TableCell>
                  {offer.last_run ? new Date(offer.last_run).toLocaleString('es-CL') : 'Nunca'}
                </TableCell>
                <TableCell>{offer.finished ? 'Sí' : 'No'}</TableCell>
                <TableCell>
                  <AutomatedOfferViewer automated_task_offer_id={offer.id} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-pink-100 p-4 rounded-lg">
            <h2 className="text-xl font-bold mb-4">Sin Experiencia</h2>
            {offers.filter((o) => o.current_step === 'no-exp' && !o.finished).map(renderOfferCard)}
          </div>
          <div className="bg-blue-100 p-4 rounded-lg">
            <h2 className="text-xl font-bold mb-4">Con Experiencia</h2>
            {offers.filter((o) => o.current_step === 'exp' && !o.finished).map(renderOfferCard)}
          </div>
          <div className="bg-green-100 p-4 rounded-lg">
            <h2 className="text-xl font-bold mb-4">Ofertas Completadas</h2>
            {offers
              .filter((o) => o.current_step === 'fully_offered' && !o.finished)
              .map(renderOfferCard)}
          </div>
          <div className="bg-purple-100 p-4 rounded-lg">
            <h2 className="text-xl font-bold mb-4">Completadas</h2>
            {offers.filter((o) => o.finished).map(renderOfferCard)}
          </div>
        </div>
      )}

      {selectedTask && (
        <ModalTask
          isOpen={isTaskModalOpen}
          onClose={() => setIsTaskModalOpen(false)}
          task={selectedTask}
          onReset={() => setSelectedTask(null)}
        />
      )}
    </div>
  );
};

export default AutomatedOffersPage;
